import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { debounceTime, filter, Subject, switchMap, takeUntil } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AppService } from '../../../app.service';
import { GlobalService } from '../../../global/services/global.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AdminFactoryService } from '../../../global/services/admin-factory.service';
import { TemplateAssignService } from './template-assign.service';
import { UserAssignModalComponent } from '../../../users-component/user-assign-modal/user-assign-modal.component';
import { TemplateEditComponent } from '../templates/template-edit/template-edit.component';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-template-assign',
    templateUrl: './template-assign.component.html',
    styleUrls: ['./template-assign.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TemplateAssignComponent implements OnInit, OnDestroy {
  activeTab = 0;
  showImport = false;
  showFormLoader = false;
  clients: any[] = [];
  selectedAppClient: any = null;
  selectedClientName = '';
  searchText = '';
  searchTerm = '';
  templatesGroup: { [key: string]: any[] } = {};
  taskModel: any = null;
  userList: any[] = [];
  assignUserList: any[] = [];
  unassignUserList: any[] = [];
  showModulesListing = false;
  template_data: { template_name?: string } = {};
  totalModulesAssigned = 0;
  totalModulesForSelectedClient = 0;
  userAssignNav: any[] = [];
  selectedApplication: any = null;
  filteredUserAssignNav: any[] = [];
  moduleColumns: any[][] = [[], []];
  showTemplateLoader = false;
  showModuleLoader = false;
  selectedTemplates: { [key: string]: any } = {};
  allUserTemplates: any[] = [];
  originalUserTemplates: any[] = [];
  showOverlayScreen = false;
  allExpanded = false;
  applicationFilter: any = { values: [], selected: [] };
  groupNameMap: { [key: string]: string } = {};
  templateGroups: { [name: string]: any } = {};
  userSearchTerm = '';
  userSearchResults: any[] = [];
  unassignSearchTerm = '';
  unassignSearchResults: any[] = [];
  unassignedTemplatesList: string[] = [];
  assignSearching = false;
  assignNoResults = false;
  unassignSearching = false;
  unassignNoResults = false;
  saveDisabled = true;
  rowPrimaryKey: any = 0;
  moduleSaveLoading = false;

  private baseUrl = '';
  private adminApiUrl = '';
  private assignSearchSubject = new Subject<string>();
  private unassignSearchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private cdr: ChangeDetectorRef,
    private appService: AppService,
    private globalService: GlobalService,
    private gtwSnackbarService: GtwSnackbarService,
    private templateAssignService: TemplateAssignService,
    private adminFactory: AdminFactoryService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.baseUrl = environment.base_url;
    this.adminApiUrl = environment.admin_api_url;

    const userSettings = this.appService.USER_SETTINGS;
    this.clients = userSettings?.user?.clients || [];
    const selectedClientKey = userSettings?.user?.client?.client_key;
    this.selectedAppClient = this.clients.find((c: any) => String(c.client_key) === String(selectedClientKey))
      || this.clients[0] || null;

    this.showImport = String(this.getGlobalParam('is_locked') ?? 'N') === 'Y';
    this.activeTab = this.canEditTemplateTab() ? 1 : 2;
    this.loadChannel(this.selectedAppClient?.client_key);

    this.assignSearchSubject.pipe(
      debounceTime(300),
      filter((val: string) => val.length >= 3),
      switchMap((val: string) => {
        this.assignSearching = true;
        this.assignNoResults = false;
        this.cdr.detectChanges();
        return this.templateAssignService.searchUsers(this.baseUrl, val);
      }),
      takeUntil(this.destroy$)
    ).subscribe(
      (resp: any) => {
        this.assignSearching = false;
        const users = resp?.jsonObject || [];
        if (users.length > 0) {
          this.userSearchResults = users.map((u: any) => ({
            name: u.NAME || u.name || '',
            userName: u.NAME || u.name || '',
            ssoId: u.VALUE || u.value || '',
          }));
          this.assignNoResults = false;
        } else {
          this.userSearchResults = [];
          this.assignNoResults = true;
        }
        this.cdr.detectChanges();
      },
      () => {
        this.assignSearching = false;
        this.userSearchResults = [];
        this.cdr.detectChanges();
      }
    );

    this.unassignSearchSubject.pipe(
      debounceTime(300),
      filter((val: string) => val.length >= 3),
      switchMap((val: string) => {
        this.unassignSearching = true;
        this.unassignNoResults = false;
        this.cdr.detectChanges();
        return this.templateAssignService.searchUsers(this.baseUrl, val);
      }),
      takeUntil(this.destroy$)
    ).subscribe(
      (resp: any) => {
        this.unassignSearching = false;
        const users = resp?.jsonObject || [];
        if (users.length > 0) {
          this.unassignSearchResults = users.map((u: any) => ({
            name: u.NAME || u.name || '',
            userName: u.NAME || u.name || '',
            ssoId: u.VALUE || u.value || '',
          }));
          this.unassignNoResults = false;
        } else {
          this.unassignSearchResults = [];
          this.unassignNoResults = true;
        }
        this.cdr.detectChanges();
      },
      () => {
        this.unassignSearching = false;
        this.unassignSearchResults = [];
        this.cdr.detectChanges();
      }
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------------------------------------------------------------------
  // Global params helper
  // ---------------------------------------------------------------------------

  private get globalParams(): any {
    return this.globalService.global?.globalParams || {};
  }

  private get globalServiceObj(): any {
    return { globalParams: this.globalParams };
  }

  private getGlobalParam(key: string): any {
    return this.globalParams?.[key];
  }

  private get userAccessLevel(): number {
    return this.appService.USER_SETTINGS?.user?.system_access_level || 0;
  }

  private get userSsoId(): string {
    return this.appService.USER_SETTINGS?.user?.sso_id || '';
  }

  private hasSelectedClientKey(): boolean {
    const ck = this.selectedAppClient?.client_key;
    return ck != null && ck !== '';
  }

  // ---------------------------------------------------------------------------
  // Tab navigation
  // ---------------------------------------------------------------------------

  canEditTemplateTab(): boolean {
    return this.userAccessLevel === 5;
  }

  openTab(tab: number): void {
    if ((tab === 2 || tab === 3) && this.showImport) return;
    if (tab === 1 && !this.canEditTemplateTab()) return;
    this.activeTab = tab;
    this.cdr.detectChanges();
  }

  // ---------------------------------------------------------------------------
  // Template list (left panel)
  // ---------------------------------------------------------------------------

  loadChannel(clientKey: string): void {
    if (clientKey == null || clientKey === '') return;

    this.selectedClientName = this.selectedAppClient?.client_name || '';
    this.showTemplateLoader = true;
    this.showModulesListing = false;
    this.taskModel = null;
    this.userAssignNav = [];
    this.filteredUserAssignNav = [];
    this.userList = [];
    this.cdr.detectChanges();

    this.adminFactory.getAppKeys(clientKey).pipe(takeUntil(this.destroy$)).subscribe((appKeysResp: any) => {
      const channelList = appKeysResp?.channel?.p_cursor || [];
      const selectedChannel = channelList[0];
      if (!selectedChannel?.channel_key) {
        this.showTemplateLoader = false;
        this.cdr.detectChanges();
        return;
      }

      this.adminFactory.getConfigSettings(selectedChannel.channel_key).pipe(takeUntil(this.destroy$)).subscribe((configResp: any) => {
        const configSettings: any[] = configResp?.configSettings || [];
        const tempGroupObj = configSettings.find((c: any) => c.NAME === 'access_template_groups');
        if (tempGroupObj?.VALUE) {
          try { this.templateGroups = safeJsonInput<any>(tempGroupObj.VALUE, null, 'template-assign.parse1'); } catch { this.templateGroups = {}; }
        }

        this.templateAssignService
          .getAllTemplates(this.baseUrl, String(clientKey), this.globalServiceObj, {}).pipe(takeUntil(this.destroy$))
          .subscribe((resp: any) => {
            this.showTemplateLoader = false;
            if (String(resp?.callSuccess) === '1') {
              const list = resp?.jsonObject || [];
              this.templatesGroup = this.groupBy(list, 'TEMPLATE_GROUP_KEY');
              this.groupNameMap = {};
              Object.keys(this.templatesGroup).forEach((k) => {
                this.groupNameMap[k] = this.resolveGroupName(k);
              });
            } else {
              this.templatesGroup = {};
              this.gtwSnackbarService.openSnackBar('Unable to load templates.', 'gtw-snackbar--red');
            }
            this.cdr.detectChanges();
          });
      });
    });
  }

  get filteredTemplatesGroup(): { [key: string]: any[] } {
    const source = this.searchText ? this.filterTemplatesBySearch() : this.templatesGroup;
    return this.sortTemplateGroups(source);
  }

  private filterTemplatesBySearch(): { [key: string]: any[] } {
    const term = this.searchText.toLowerCase();
    const filtered: { [key: string]: any[] } = {};
    Object.keys(this.templatesGroup || {}).forEach((key) => {
      const rows = (this.templatesGroup[key] || []).filter((row: any) =>
        String(row?.TEMPLATE_NAME || '').toLowerCase().includes(term)
      );
      if (rows.length > 0) filtered[key] = rows;
    });
    return filtered;
  }

  private sortTemplateGroups(groups: { [key: string]: any[] }): { [key: string]: any[] } {
    const keys = Object.keys(groups || {}).sort((a, b) => {
      const aUncat = !a || a === 'null' || a === 'undefined';
      const bUncat = !b || b === 'null' || b === 'undefined';
      if (aUncat && !bUncat) return 1;
      if (!aUncat && bUncat) return -1;
      return this.getGroupName(a).localeCompare(this.getGroupName(b));
    });
    const sorted: { [key: string]: any[] } = {};
    keys.forEach((k) => { sorted[k] = groups[k]; });
    return sorted;
  }

  getGroupName(key: string): string {
    return this.groupNameMap[key] || key || '';
  }

  private resolveGroupName(groupKey: string): string {
    if (!groupKey || groupKey === 'null' || groupKey === 'undefined') {
      return 'Uncategorized';
    }
    // Reverse lookup: templateGroups is { "Common": 11, "Domestic": 12, ... }
    const name = Object.keys(this.templateGroups || {}).find(
      (k) => String(this.templateGroups[k]) === String(groupKey)
    );
    return name || groupKey || 'Uncategorized';
  }

  // ---------------------------------------------------------------------------
  // Tab 1 — Edit Template
  // ---------------------------------------------------------------------------

  selectTaskModel(template: any): void {
    this.taskModel = template;
    this.getTemplateData(template);
  }

  getTemplateData(template: any): void {
    if (!template?.TEMPLATE_KEY || !this.hasSelectedClientKey()) return;

    this.showModuleLoader = true;
    this.showModulesListing = false;
    this.rowPrimaryKey = template.TEMPLATE_KEY;
    this.template_data = { template_name: template.TEMPLATE_NAME || '' };
    const clientKey = String(this.selectedAppClient.client_key);
    const templateKey = String(template.TEMPLATE_KEY);

    this.templateAssignService
      .getTemplateUsers(this.baseUrl, clientKey, templateKey, this.globalServiceObj, {}).pipe(takeUntil(this.destroy$))
      .subscribe((usersResp: any) => {
        this.userList = usersResp?.jsonObject || [];
        this.cdr.detectChanges();
      });

    this.templateAssignService
      .getTemplateModules(this.adminApiUrl, clientKey, templateKey).pipe(takeUntil(this.destroy$))
      .subscribe((modulesResp: any) => {
        this.showModuleLoader = false;
        if (String(modulesResp?.callSuccess) === '1') {
          this.userAssignNav = modulesResp?.jsonObject || [];
          this.applicationFilter.values = this.userAssignNav.map((item: any) => ({ ...item, value: item.id }));
          this.applicationFilter.selected = [...this.applicationFilter.values];
          this.mergeNonMatchedTaskHierarchy(this.userAssignNav);
          this.initDefaultRoles(this.userAssignNav);
          this.initDefaultCollapsed(this.userAssignNav);
          this.cacheNodeMetadata(this.userAssignNav);
          this.updateModuleCounts();
          const defaultApp = this.userAssignNav.find((x: any) => String(x.id) === '5') || this.userAssignNav[0] || null;
          this.setApplication(defaultApp);
          this.refreshExpandedState();
          this.getTotalModulesFound();
          this.totalModulesAssigned = this.countAssignedModules(this.filteredUserAssignNav);
          this.showModulesListing = true;
        } else {
          this.gtwSnackbarService.openSnackBar('Unable to load template modules.', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      });
  }

  setApplication(app: any): void {
    this.selectedApplication = app;
    this.filteredUserAssignNav = Array.isArray(app?.children) ? app.children : [];
    this.distributeToColumns();
    this.refreshExpandedState();
    this.cdr.detectChanges();
  }

  private distributeToColumns(): void {
    const columns: any[][] = [[], []];
    (this.filteredUserAssignNav || []).forEach((node, index) => {
      columns[index % 2].push(node);
    });
    this.moduleColumns = columns;
  }

  private refreshExpandedState(): void {
    this.allExpanded = (this.filteredUserAssignNav || []).some((node: any) => !node.collapsed);
  }

  trackByNodeId(_index: number, node: any): any {
    return node?.id ?? node?.jcd_ta_key ?? _index;
  }

  trackByIndex(index: number): number {
    return index;
  }

  toggleAll(): void {
    const shouldCollapse = this.allExpanded;
    (this.filteredUserAssignNav || []).forEach((node: any) => { node.collapsed = shouldCollapse; });
    this.allExpanded = !shouldCollapse;
    this.cdr.detectChanges();
  }

  toggleNode(node: any): void {
    node.collapsed = !node.collapsed;
    this.refreshExpandedState();
    this.cdr.detectChanges();
  }

  openUserAccessModal(node: any): void {
    const dialogRef = this.dialog.open(UserAssignModalComponent, {
      width: '600px',
      disableClose: true,
      panelClass: ['assign-user-dialog', 'increase-z-index'],
      position: { top: '25px' },
      data: { taskKey: node.id }
    });
    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: any) => {
      if (result?.saved) {
        this.getTemplateData(this.taskModel);
      }
      this.cdr.detectChanges();
    });
  }

  filterMenu(term: string): void {
    this.searchTerm = term || '';
    if (this.selectedApplication?.children) {
      this.filteredUserAssignNav = this.filterTree([...this.selectedApplication.children], this.searchTerm);
      this.totalModulesAssigned = this.countAssignedModules(this.filteredUserAssignNav);
    }
    this.distributeToColumns();
    this.refreshExpandedState();
    this.cdr.detectChanges();
  }

  // ---------------------------------------------------------------------------
  // Tab 1 — Create Template modal
  // ---------------------------------------------------------------------------

  addTemplate(): void {
    const config = new MatDialogConfig();
    config.panelClass = 'admin-modal-panel';
    config.width = '90vw';
    config.maxWidth = '90vw';
    config.disableClose = true;
    config.data = { rowData: {}, mode: 'new' };

    const ref = this.dialog.open(TemplateEditComponent, config);
    ref.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        if (result === 'saved') {
          this.loadChannel(this.selectedAppClient?.client_key);
        }
      });
  }

  // ---------------------------------------------------------------------------
  // Tab 1 — Module roles & save
  // ---------------------------------------------------------------------------

  getNodeRoleValue(node: any): any {
    return node?.role?.value ?? '';
  }

  onNodeRoleValueChange(node: any, value: any): void {
    const role = this.findRoleByValue(node, value);
    if (!role) return;
    node.role = role;
    this.updateModuleCounts();
    this.saveRoleChanges([node]);
  }

  applyGroupRole(mode: string, node: any): void {
    const changed: any[] = [];
    const walk = (n: any): void => {
      if (!n) return;
      if (Array.isArray(n.roles) && n.roles.length > 0) {
        const nextRole = this.resolveRoleByMode(n, mode);
        if (nextRole) { n.role = nextRole; changed.push(n); }
      }
      if (Array.isArray(n.children)) n.children.forEach((child: any) => walk(child));
    };
    walk(node);
    this.updateModuleCounts();
    this.saveRoleChanges(changed);
  }

  saveModuleAssignments(): void {
    if (!this.rowPrimaryKey) {
      this.gtwSnackbarService.openSnackBar('Select a template before saving modules.', 'gtw-snackbar--yellow');
      return;
    }
    this.moduleSaveLoading = true;
    this.showOverlayScreen = true;
    const payloadNodes = this.buildModulePayload(this.userAssignNav || []);
    if (payloadNodes.length === 0) {
      this.moduleSaveLoading = false;
      this.showOverlayScreen = false;
      this.gtwSnackbarService.openSnackBar('No module role changes available to save.', 'gtw-snackbar--yellow');
      this.cdr.detectChanges();
      return;
    }
    this.templateAssignService
      .saveModuleAccess(this.adminApiUrl, String(this.rowPrimaryKey), payloadNodes).pipe(takeUntil(this.destroy$))
      .subscribe(
        (resp: any) => {
          this.moduleSaveLoading = false;
          this.showOverlayScreen = false;
          if (String(resp?.callSuccess) === '1') {
            this.gtwSnackbarService.openSnackBar('Data saved successfully.', 'gtw-snackbar--green');
          } else {
            this.gtwSnackbarService.openSnackBar(resp?.errorMessage || 'Unable to save module access.', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        () => {
          this.moduleSaveLoading = false;
          this.showOverlayScreen = false;
          this.gtwSnackbarService.openSnackBar('Unable to save module access.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      );
  }

  // ---------------------------------------------------------------------------
  // Tab 2 — Assign Template
  // ---------------------------------------------------------------------------

  onAssignUserSearch(): void {
    this.userSearchResults = [];
    this.assignNoResults = false;
    if ((this.userSearchTerm || '').length < 3) {
      this.assignSearching = false;
      return;
    }
    this.assignSearchSubject.next(this.userSearchTerm);
  }

  addAssignUser(user: any): void {
    if (!user?.ssoId) return;
    if (this.assignUserList.some((u: any) => String(u.ssoId) === String(user.ssoId))) {
      this.gtwSnackbarService.openSnackBar('Email already added', 'gtw-snackbar--yellow');
      return;
    }
    this.assignUserList.push(user);
    this.userSearchTerm = '';
    this.userSearchResults = [];
    this.assignNoResults = false;
    this.cdr.detectChanges();
  }

  removeAssignUser(index: number): void {
    if (index < 0 || index >= this.assignUserList.length) return;
    this.assignUserList.splice(index, 1);
    this.cdr.detectChanges();
  }

  updateModuleParams(type: string): void {
    if (type === 'template') {
      const templateCount = Object.keys(this.selectedTemplates || {}).filter((k) => !!this.selectedTemplates[k]).length;
      this.saveDisabled = templateCount < 1;
    }
    this.cdr.detectChanges();
  }

  saveUserList(): void {
    const userSsoIds = (this.assignUserList || []).map((u: any) => u.ssoId).filter((x: any) => !!x);
    const selectedTemplateIds = Object.keys(this.selectedTemplates || {})
      .filter((k) => !!this.selectedTemplates[k])
      .map((k) => this.selectedTemplates[k]);
    if (userSsoIds.length === 0 || selectedTemplateIds.length === 0 || !this.hasSelectedClientKey()) {
      this.gtwSnackbarService.openSnackBar('Select templates and users before save.', 'gtw-snackbar--yellow');
      return;
    }
    const clobData = { users: userSsoIds, templates: selectedTemplateIds, isFinalList: 'N' };
    this.templateAssignService
      .saveMultiTemplateUser(this.baseUrl, String(this.selectedAppClient.client_key), this.globalServiceObj, clobData, {}).pipe(takeUntil(this.destroy$))
      .subscribe((resp: any) => {
        if (String(resp?.callSuccess) === '1') {
          this.gtwSnackbarService.openSnackBar('Multiple templates and users mapped successfully', 'gtw-snackbar--green');
        } else {
          this.gtwSnackbarService.openSnackBar(resp?.errorMessage || 'Save failed.', 'gtw-snackbar--red');
        }
      });
    this.cdr.detectChanges();
  }

  resetUserList(): void {
    this.assignUserList = [];
    this.selectedTemplates = {};
    this.saveDisabled = true;
    this.cdr.detectChanges();
  }

  // ---------------------------------------------------------------------------
  // Tab 3 — Unassign Template
  // ---------------------------------------------------------------------------

  onUnassignUserSearch(): void {
    this.unassignSearchResults = [];
    this.unassignNoResults = false;
    if ((this.unassignSearchTerm || '').length < 3) {
      this.unassignSearching = false;
      return;
    }
    this.unassignSearchSubject.next(this.unassignSearchTerm);
  }

  selectUnassignUser(user: any): void {
    if (!user?.ssoId || !this.hasSelectedClientKey()) return;
    this.unassignUserList = [user];
    this.unassignSearchTerm = '';
    this.unassignSearchResults = [];
    this.unassignNoResults = false;
    this.showTemplateLoader = true;
    this.templateAssignService
      .getUserTemplates(this.baseUrl, String(this.selectedAppClient.client_key), String(user.ssoId), this.globalServiceObj, {}).pipe(takeUntil(this.destroy$))
      .subscribe((resp: any) => {
        this.showTemplateLoader = false;
        if (String(resp?.callSuccess) === '1') {
          this.allUserTemplates = resp?.jsonObject || [];
          this.originalUserTemplates = [...this.allUserTemplates];
          this.unassignedTemplatesList = [];
        } else {
          this.allUserTemplates = [];
          this.originalUserTemplates = [];
          this.gtwSnackbarService.openSnackBar(resp?.errorMessage || 'Unable to load user templates.', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      });
  }

  onUnassignClientChange(client: any): void {
    if (!client) return;
    this.selectedAppClient = client;
    this.clearUser();
    this.loadChannel(client.client_key);
  }

  removeTemplateAt(index: number): void {
    const item = this.allUserTemplates[index];
    if (!item) return;
    this.unassignedTemplatesList.push(String(item.TEMPLATE_KEY));
    this.allUserTemplates.splice(index, 1);
    this.cdr.detectChanges();
  }

  clearUser(): void {
    this.unassignUserList = [];
    this.allUserTemplates = [];
    this.originalUserTemplates = [];
    this.unassignedTemplatesList = [];
    this.cdr.detectChanges();
  }

  saveTemplateList(): void {
    if (!this.unassignUserList[0]?.ssoId || this.unassignedTemplatesList.length === 0 || !this.hasSelectedClientKey()) {
      this.gtwSnackbarService.openSnackBar('No template changes to save.', 'gtw-snackbar--yellow');
      return;
    }
    const clobData = {
      user_sso: this.unassignUserList[0].ssoId,
      unassigned_template_keys: this.unassignedTemplatesList.join(','),
    };
    this.templateAssignService
      .saveUserTemplates(this.baseUrl, String(this.selectedAppClient.client_key), clobData, this.globalServiceObj, {}).pipe(takeUntil(this.destroy$))
      .subscribe((resp: any) => {
        if (String(resp?.callSuccess) === '1') {
          this.gtwSnackbarService.openSnackBar('Templates unassigned successfully', 'gtw-snackbar--green');
        } else {
          this.gtwSnackbarService.openSnackBar(resp?.errorMessage || 'Save failed.', 'gtw-snackbar--red');
        }
      });
    this.cdr.detectChanges();
  }

  resetTemplateList(): void {
    this.allUserTemplates = [...this.originalUserTemplates];
    this.unassignedTemplatesList = [];
    this.cdr.detectChanges();
  }

  // ---------------------------------------------------------------------------
  // Template helpers
  // ---------------------------------------------------------------------------

  isArray(value: any): boolean {
    return Array.isArray(value);
  }

  isLeafNode(node: any): boolean {
    return !Array.isArray(node?.children) || node.children.length === 0;
  }

  moduleNodeLabel(node: any): string {
    return String(node?.name || node?.task_name || node?.TEMPLATE_NAME || '');
  }

  isNodeSelected(node: any): boolean {
    return !!(node?.assigned ?? node?.is_assigned ?? node?.selected ?? node?.hasAccess);
  }

  onNodeSelectionChange(node: any, checked: boolean): void {
    if (!node) return;
    node.assigned = checked;
    node.selected = checked;
    node.is_assigned = checked;
    node.hasAccess = checked;
    if (Array.isArray(node.children)) {
      node.children.forEach((child: any) => this.onNodeSelectionChange(child, checked));
    }
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private groupBy(items: any[], key: string): { [key: string]: any[] } {
    return (items || []).reduce((acc: any, item: any) => {
      const k = String(item?.[key] ?? '');
      if (!acc[k]) acc[k] = [];
      acc[k].push(item);
      return acc;
    }, {});
  }

  private filterTree(nodes: any[], term: string): any[] {
    const t = (term || '').toLowerCase();
    if (!t) return nodes || [];
    return (nodes || []).filter((item: any) => {
      if (Array.isArray(item.children)) {
        item.children = this.filterTree(item.children, term);
      }
      const name = String(item?.name || item?.task_name || '').toLowerCase();
      return name.includes(t) || (item.children && item.children.length > 0);
    });
  }

  private countAssignedModules(nodes: any[]): number {
    let count = 0;
    const walk = (list: any[]) => {
      (list || []).forEach((item: any) => {
        if (String(item?.type || '').toLowerCase() === 'activity') count++;
        if (Array.isArray(item?.children)) walk(item.children);
      });
    };
    walk(nodes || []);
    return count;
  }

  private getTotalModulesFound(): void {
    this.totalModulesForSelectedClient = this.countAssignedModules(this.userAssignNav || []);
  }

  private cacheNodeMetadata(nodes: any[]): void {
    for (const node of nodes) {
      node._tooltip = (node.ownerList?.length > 0)
        ? node.ownerList.map((u: any) => u.name).join(', ')
        : 'No Users Assigned';
      if (node.children) this.cacheNodeMetadata(node.children);
    }
  }

  private mergeNonMatchedTaskHierarchy(jsonAry: any[]): void {
    for (let i = 0; i < jsonAry.length; i++) {
      const chlObj = jsonAry[i];
      if (chlObj.children && chlObj.children.length) {
        for (let c = 0; c < chlObj.children.length; c++) {
          const childTaskObj = chlObj.children[c];
          const childObjList = chlObj.children.filter((x: any) => x.parentTaskKey === childTaskObj.id);
          if (childObjList.length) {
            childTaskObj.processed = true;
            for (const childObj of childObjList) {
              if (childTaskObj.children) {
                childTaskObj.children.unshift(childObj);
              } else {
                childTaskObj.children = [childObj];
              }
            }
          }
        }
      }
      chlObj.children = (chlObj.children || []).filter((x: any) => x.parentTaskKey === null || x.parentTaskKey === undefined);
    }
  }

  private initDefaultRoles(nodes: any[]): void {
    for (const node of nodes) {
      if (node.roles?.length > 0) {
        if (!node.role) {
          node.role = node.roles[0];
        } else {
          const match = node.roles.find((r: any) => String(r.value) === String(node.role.value));
          if (match) node.role = match;
        }
      }
      if (node.children) this.initDefaultRoles(node.children);
    }
  }

  private initDefaultCollapsed(nodes: any[]): void {
    for (const node of nodes) {
      node.collapsed = true;
      node._bulkDropdown = '';
      if (node.children) this.initDefaultCollapsed(node.children);
    }
  }

  updateModuleCounts(): void {
    for (const app of this.userAssignNav) {
      this.computeCounts(app);
    }
  }

  private computeCounts(node: any): { assigned: number; total: number } {
    if (!node.children || node.children.length === 0) {
      node._assigned = 0;
      node._total = 0;
      node._badgeClass = 'badge-partial';
      return { assigned: 0, total: 0 };
    }

    let assigned = 0;
    let total = 0;

    for (const child of node.children) {
      if (child?.role && child.role.value > 0) assigned++;
      if (child?.role) total++;
      if (child?.children?.length > 0) {
        const childCounts = this.computeCounts(child);
        assigned += childCounts.assigned;
        total += childCounts.total;
      } else if (child) {
        child._assigned = 0;
        child._total = 0;
        child._badgeClass = 'badge-partial';
      }
    }

    node._assigned = assigned;
    node._total = total;
    node._badgeClass = (assigned === total && total > 0) ? 'badge-complete' : 'badge-partial';
    return { assigned, total };
  }

  private saveRoleChanges(changedNodes: any[]): void {
    if (!this.rowPrimaryKey || !changedNodes || changedNodes.length === 0) return;
    const payloadNodes = this.mapNodesToRolePayload(changedNodes);
    if (payloadNodes.length === 0) return;
    this.moduleSaveLoading = true;
    this.templateAssignService
      .saveModuleAccess(this.adminApiUrl, String(this.rowPrimaryKey), payloadNodes).pipe(takeUntil(this.destroy$))
      .subscribe(
        (resp: any) => {
          this.moduleSaveLoading = false;
          if (String(resp?.callSuccess) === '1') {
            this.gtwSnackbarService.openSnackBar('Data saved successfully.', 'gtw-snackbar--green');
          } else {
            this.gtwSnackbarService.openSnackBar(resp?.errorMessage || 'Unable to save module access.', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        () => {
          this.moduleSaveLoading = false;
          this.gtwSnackbarService.openSnackBar('Unable to save module access.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      );
  }

  private buildModulePayload(nodes: any[]): any[] {
    const list: any[] = [];
    const walk = (arr: any[]) => {
      (arr || []).forEach((node: any) => {
        if (node?.jcd_ta_key && node?.role?.value !== undefined && node?.role?.value !== null) list.push(node);
        if (Array.isArray(node?.children)) walk(node.children);
      });
    };
    walk(nodes || []);
    return this.mapNodesToRolePayload(list);
  }

  private mapNodesToRolePayload(nodes: any[]): any[] {
    const clientKey = this.selectedAppClient?.client_key ?? '';
    return (nodes || [])
      .filter((node: any) => !!node?.jcd_ta_key && node?.role?.value !== undefined && node?.role?.value !== null)
      .map((node: any) => ({
        jcd_ta_key: node.jcd_ta_key,
        jcd_ta_role_key: node.role.value,
        user_id: this.userSsoId,
        client_key: clientKey,
      }));
  }

  private findRoleByValue(node: any, value: any): any {
    if (!Array.isArray(node?.roles)) return null;
    return node.roles.find((role: any) => String(role?.value) === String(value)) || null;
  }

  private resolveRoleByMode(node: any, mode: string): any {
    if (!Array.isArray(node?.roles) || node.roles.length === 0) return null;
    if (mode === '1') {
      const highest = Math.max(...node.roles.map((r: any) => Number(r.level ?? 0)));
      return node.roles.find((r: any) => Number(r.level ?? 0) === highest) || null;
    }
    if (mode === '-1') {
      const valid = node.roles.map((r: any) => Number(r.level ?? 0)).filter((level: number) => level !== 0);
      if (valid.length === 0) return node.roles.find((r: any) => Number(r.level ?? 0) === 0) || null;
      const lowest = Math.min(...valid);
      return node.roles.find((r: any) => Number(r.level ?? 0) === lowest) || null;
    }
    return node.roles.find((r: any) => Number(r.level ?? 0) === 0) || node.roles[0];
  }
}
